import { Checkbox, Tooltip, Typography } from '@mui/material'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import { observer } from 'mobx-react-lite'
import type { SxProps, Theme } from '@mui/material/styles'
import { AppBaseLinkKind } from 'src/component/common/analog/molecule/link/AppBaseLinkKind'
import { AppInternalLink } from 'src/component/common/analog/molecule/link/AppInternalLink'
import { Word } from 'src/feature/localization/component/atom/Word'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import { handlePassedSx } from 'src/feature/theme/model/Sx'
import { ColorTokens } from 'src/feature/theme/model/Palette'
import type { TodoChecklistItem } from './todo-checklist.helper'
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded'

export type TodoChecklistProps = {
  title: string
  items: TodoChecklistItem[]
  allCompleted?: boolean
  showCheckboxWhenAllCompleted?: boolean
  enforceSequentialActions?: boolean
  disabledActionTooltip?: string
  rootSx?: SxProps<Theme>
  titleSx?: SxProps<Theme>
  listSx?: SxProps<Theme>
  itemSx?: SxProps<Theme>
  contentSx?: SxProps<Theme>
}

export const TodoChecklist = observer(function TodoChecklist({
  title,
  items,
  allCompleted = false,
  showCheckboxWhenAllCompleted = true,
  enforceSequentialActions = false,
  disabledActionTooltip,
  rootSx,
  titleSx,
  listSx,
  itemSx,
  contentSx,
}: TodoChecklistProps) {
  const shouldRenderCheckbox = showCheckboxWhenAllCompleted || !allCompleted
  const titleStyles = [styles.title, ...handlePassedSx(titleSx)]
  let hasIncompletePreviousStep = false

  const renderDisabledAction = (actionContent: React.ReactNode) => {
    const disabledAction = (
      <AppBaseLinkKind kind='buttonLikeLink' disabled sx={[styles.link, styles.disabledLink]}>
        {actionContent}
      </AppBaseLinkKind>
    )

    if (!disabledActionTooltip) {
      return disabledAction
    }

    return (
      <Tooltip title={disabledActionTooltip} arrow>
        <span>{disabledAction}</span>
      </Tooltip>
    )
  }

  const renderTodoText = (item: TodoChecklistItem, isActionDisabled: boolean) => {
    if (item.completed) {
      return item.completedLabel
    }

    const actionContent = <Word sx={styles.actionText}>{item.actionLabel}</Word>

    if (isActionDisabled) {
      return (
        <>
          {item.pendingLabel}{' '}
          {renderDisabledAction(actionContent)}
        </>
      )
    }

    if (item.onAction) {
      return (
        <>
          {item.pendingLabel}{' '}
          <AppBaseLinkKind kind='buttonLikeLink' onClick={item.onAction} sx={styles.link}>
            {actionContent}
          </AppBaseLinkKind>
        </>
      )
    }

    if (!item.to) {
      return (
        <>
          {item.pendingLabel}{' '}
          {actionContent}
        </>
      )
    }

    return (
      <>
        {item.pendingLabel}{' '}
        <AppInternalLink to={item.to} state={item.state} sx={styles.link}>
          {actionContent}
        </AppInternalLink>
      </>
    )
  }

  const renderLeadingIndicator = (item: TodoChecklistItem, isActionDisabled: boolean) => {
    if (isActionDisabled && !item.completed) {
      return <AppBox sx={styles.lockBox}><LockOutlinedIcon sx={styles.lockIcon} /></AppBox>
    }

    if (!shouldRenderCheckbox) {
      return null
    }

    return <Checkbox
      icon={<TaskAltRoundedIcon />}
      checkedIcon={<TaskAltRoundedIcon />}
      checked={item.completed}
      size='small'
      disableRipple
      tabIndex={-1}
      sx={styles.checkbox}
    />
  }

  return (
    <AppBox sx={[styles.root, rootSx]}>
      <Typography variant='h4' sx={titleStyles}>{title}</Typography>
      <AppBox component='ul' sx={[styles.list, listSx]}>
        {items.map((item) => {
          const isActionDisabled = enforceSequentialActions && hasIncompletePreviousStep

          if (!item.completed) {
            hasIncompletePreviousStep = true
          }

          return (
            <AppBox component='li' key={item.key} sx={[styles.item, itemSx]}>
              {renderLeadingIndicator(item, isActionDisabled)}
              <AppBox sx={[styles.content, contentSx]}>
                <Typography sx={[styles.text, item.completed ? styles.textCompleted : null]}>
                  {renderTodoText(item, isActionDisabled)}
                </Typography>
              </AppBox>
            </AppBox>
          )
        })}
      </AppBox>
    </AppBox>
  )
})

const TODO_LINK_FONT_SIZE = 14

const styles = {
  root: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 10,
  },
  title: {
    width: '100%',
    margin: 0,
    lineHeight: 1.2,
  },
  list: {
    width: '100%',
    margin: 0,
    padding: 0,
    listStyle: 'none',
    display: 'flex',
    flexDirection: 'column',
    gap: 5,
    '& > li:not(:last-of-type)': {
      paddingBottom: '8px',
      borderBottom: '1px solid',
      borderBottomColor: 'grey.100',
    },
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    minWidth: 0,
  },
  content: {
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: '2px',
  },
  checkbox: {
    padding: 0,
    alignSelf: 'center',
    marginTop: 0,
    color: ColorTokens.neutral300,
    '&.Mui-checked': {
      color: ColorTokens.green200,
    },
  },
  text: {
    fontSize: 12,
    lineHeight: 1.4,
    color: 'text.primary',
  },
  textCompleted: {
    color: 'text.secondary',
    fontWeight: 500,
    textDecoration: 'line-through',
    textDecorationThickness: '1px',
  },
  link: {
    fontSize: TODO_LINK_FONT_SIZE,
    lineHeight: 1.4,
    display: 'inline-flex',
    alignItems: 'center',
    verticalAlign: 'baseline',
  },
  disabledLink: {
    color: ColorTokens.neutral600,
    cursor: 'not-allowed',
    textDecoration: 'none',
    opacity: 0.95,
  },
  lockBox: {
    width: 20,
    height: 20,
    minWidth: 20,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '999px',
    border: '1.5px solid',
    borderColor: ColorTokens.neutral300,
    color: ColorTokens.neutral600,
    cursor: 'pointer',
  },
  lockIcon: {
    fontSize: 12,
    color: 'inherit',
  },
  actionText: {
    color: ColorTokens.primary500,
    fontSize: 'inherit',
    lineHeight: 'inherit',
    fontWeight: 'inherit',
    '.Mui-disabled &': {
      color: 'inherit',
      textDecoration: 'none',
    },
  },
} as const
